import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

// Interface voor een taak
interface KanbanTask {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'inProgress' | 'done';
  createdAt: string;
  updatedAt: string;
}

// Interface voor de widget properties
interface KanbanWidgetProps {
  title?: string;
  tasks?: KanbanTask[];
}

// Helper functie voor unieke IDs
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Status labels voor de kolommen
const STATUS_LABELS = {
  todo: 'Te doen',
  inProgress: 'In uitvoering',
  done: 'Afgerond'
};

const KanbanWidget: React.FC<KanbanWidgetProps> = ({
  title = 'Taken',
  tasks: initialTasks = []
}) => {
  // State voor de taken
  const [tasks, setTasks] = useState<KanbanTask[]>(() => {
    // Probeer taken uit localStorage te laden
    const savedTasks = localStorage.getItem('kanban_tasks');
    if (savedTasks) {
      return JSON.parse(savedTasks);
    }
    // Anders gebruik initialTasks of een lege array
    return initialTasks.length > 0 ? initialTasks : [
      {
        id: generateId(),
        title: 'Voorbeeld taak 1',
        description: 'Dit is een voorbeeld taak',
        status: 'todo',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: generateId(),
        title: 'Voorbeeld taak 2',
        description: 'Deze taak is in uitvoering',
        status: 'inProgress',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: generateId(),
        title: 'Voorbeeld taak 3',
        description: 'Deze taak is afgerond',
        status: 'done',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
  });

  // State voor de weergavemodus (kanban of lijst)
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  
  // State voor het formulier om nieuwe taken toe te voegen
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskStatus, setNewTaskStatus] = useState<'todo' | 'inProgress' | 'done'>('todo');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // State voor het bewerken van een taak
  const [editingTask, setEditingTask] = useState<KanbanTask | null>(null);
  
  // State voor drag-and-drop functionaliteit
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);

  // Opslaan naar localStorage wanneer taken veranderen
  useEffect(() => {
    localStorage.setItem('kanban_tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Task management functies
  const addTask = () => {
    if (!newTaskTitle.trim()) return;
    
    const newTask: KanbanTask = {
      id: generateId(),
      title: newTaskTitle,
      description: newTaskDescription,
      status: newTaskStatus,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setTasks([...tasks, newTask]);
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskStatus('todo');
    setShowAddForm(false);
  };
  
  const updateTask = () => {
    if (!editingTask || !editingTask.title.trim()) return;
    
    const updatedTasks = tasks.map(task => 
      task.id === editingTask.id 
        ? { ...editingTask, updatedAt: new Date().toISOString() } 
        : task
    );
    
    setTasks(updatedTasks);
    setEditingTask(null);
  };
  
  const deleteTask = (id: string) => {
    setTasks(tasks.filter(task => task.id !== id));
    if (editingTask && editingTask.id === id) {
      setEditingTask(null);
    }
  };
  
  const moveTask = (taskId: string, newStatus: 'todo' | 'inProgress' | 'done') => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId 
        ? { ...task, status: newStatus, updatedAt: new Date().toISOString() } 
        : task
    );
    
    setTasks(updatedTasks);
  };
  
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
    setDraggingTaskId(taskId);
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  
  const handleDrop = (e: React.DragEvent, status: 'todo' | 'inProgress' | 'done') => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    moveTask(taskId, status);
    setDraggingTaskId(null);
  };
  
  // Render de taken in kanban-weergave
  const renderKanbanView = () => {
    return (
      <div className="grid grid-cols-3 gap-4 mt-3">
        {/* Te doen kolom */}
        <div 
          className="bg-primary-theme-10 p-3 rounded-md"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, 'todo')}
        >
          <h3 className="font-medium text-theme mb-2">{STATUS_LABELS.todo}</h3>
          {tasks
            .filter(task => task.status === 'todo')
            .map(task => renderTaskCard(task))}
        </div>
        
        {/* In uitvoering kolom */}
        <div 
          className="bg-primary-theme-10 p-3 rounded-md"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, 'inProgress')}
        >
          <h3 className="font-medium text-theme mb-2">{STATUS_LABELS.inProgress}</h3>
          {tasks
            .filter(task => task.status === 'inProgress')
            .map(task => renderTaskCard(task))}
        </div>
        
        {/* Afgerond kolom */}
        <div 
          className="bg-primary-theme-10 p-3 rounded-md"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, 'done')}
        >
          <h3 className="font-medium text-theme mb-2">{STATUS_LABELS.done}</h3>
          {tasks
            .filter(task => task.status === 'done')
            .map(task => renderTaskCard(task))}
        </div>
      </div>
    );
  };
  
  // Render de taken in lijst-weergave
  const renderListView = () => {
    return (
      <div className="space-y-2 mt-3">
        {tasks.length === 0 ? (
          <p className="text-theme-80 text-center py-4">Geen taken beschikbaar.</p>
        ) : (
          tasks.map(task => (
            <div 
              key={task.id}
              className="flex items-center justify-between p-3 bg-primary-theme-10 rounded-md"
            >
              <div>
                <div className="flex items-center">
                  <div 
                    className={`w-2 h-2 rounded-full mr-2 ${
                      task.status === 'todo' ? 'bg-blue-500' :
                      task.status === 'inProgress' ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}
                  />
                  <h4 className="font-medium text-theme">{task.title}</h4>
                </div>
                {task.description && (
                  <p className="text-sm text-theme-80 ml-4 mt-1">{task.description}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={task.status}
                  onChange={(e) => moveTask(task.id, e.target.value as 'todo' | 'inProgress' | 'done')}
                  className="text-xs bg-primary-theme-20 text-theme border-none rounded p-1"
                >
                  <option value="todo">{STATUS_LABELS.todo}</option>
                  <option value="inProgress">{STATUS_LABELS.inProgress}</option>
                  <option value="done">{STATUS_LABELS.done}</option>
                </select>
                <button
                  onClick={() => setEditingTask(task)}
                  className="text-theme-80 hover:text-theme p-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={() => deleteTask(task.id)}
                  className="text-theme-80 hover:text-red-500 p-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };
  
  // Render een taakkaart voor de kanban weergave
  const renderTaskCard = (task: KanbanTask) => {
    return (
      <div
        key={task.id}
        draggable
        onDragStart={(e) => handleDragStart(e, task.id)}
        className={`p-3 mb-2 bg-primary-theme rounded-md shadow-sm cursor-grab ${
          draggingTaskId === task.id ? 'opacity-50' : ''
        }`}
      >
        <div className="flex justify-between items-start">
          <h4 className="font-medium text-theme">{task.title}</h4>
          <div className="flex space-x-1">
            <button
              onClick={() => setEditingTask(task)}
              className="text-theme-80 hover:text-theme"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={() => deleteTask(task.id)}
              className="text-theme-80 hover:text-red-500"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
        {task.description && (
          <p className="text-sm text-theme-80 mt-1">{task.description}</p>
        )}
        <div className="text-xs text-theme-80 mt-2">
          {new Date(task.updatedAt).toLocaleDateString('nl-NL')}
        </div>
      </div>
    );
  };
  
  // Render het formulier om een taak toe te voegen
  const renderAddTaskForm = () => {
    return (
      <div className="p-3 bg-primary-theme-10 rounded-md mt-3">
        <h3 className="font-medium text-theme mb-2">Nieuwe taak</h3>
        <div className="space-y-2">
          <input
            type="text"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            placeholder="Titel"
            className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
          />
          <textarea
            value={newTaskDescription}
            onChange={(e) => setNewTaskDescription(e.target.value)}
            placeholder="Beschrijving (optioneel)"
            className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
            rows={2}
          />
          <div>
            <label className="text-sm text-theme-80 block mb-1">Status</label>
            <select
              value={newTaskStatus}
              onChange={(e) => setNewTaskStatus(e.target.value as 'todo' | 'inProgress' | 'done')}
              className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
            >
              <option value="todo">{STATUS_LABELS.todo}</option>
              <option value="inProgress">{STATUS_LABELS.inProgress}</option>
              <option value="done">{STATUS_LABELS.done}</option>
            </select>
          </div>
          <div className="flex space-x-2 pt-2">
            <Button
              onClick={addTask}
              className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
            >
              Toevoegen
            </Button>
            <Button
              onClick={() => setShowAddForm(false)}
              variant="outline"
              className="text-theme border-theme-20"
            >
              Annuleren
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render het formulier om een taak te bewerken
  const renderEditTaskForm = () => {
    if (!editingTask) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-primary-theme border border-theme-10 rounded-lg shadow-xl w-96 p-4">
          <h3 className="font-medium text-theme mb-3">Taak bewerken</h3>
          <div className="space-y-3">
            <input
              type="text"
              value={editingTask.title}
              onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
              placeholder="Titel"
              className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
            />
            <textarea
              value={editingTask.description || ''}
              onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
              placeholder="Beschrijving (optioneel)"
              className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
              rows={3}
            />
            <div>
              <label className="text-sm text-theme-80 block mb-1">Status</label>
              <select
                value={editingTask.status}
                onChange={(e) => setEditingTask({...editingTask, status: e.target.value as 'todo' | 'inProgress' | 'done'})}
                className="w-full p-2 rounded border border-theme-10 bg-primary-theme text-theme"
              >
                <option value="todo">{STATUS_LABELS.todo}</option>
                <option value="inProgress">{STATUS_LABELS.inProgress}</option>
                <option value="done">{STATUS_LABELS.done}</option>
              </select>
            </div>
            <div className="flex space-x-2 pt-2">
              <Button
                onClick={updateTask}
                className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
              >
                Opslaan
              </Button>
              <Button
                onClick={() => setEditingTask(null)}
                variant="outline"
                className="text-theme border-theme-20"
              >
                Annuleren
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl font-semibold">{title}</CardTitle>
        <div className="flex items-center space-x-2">
          {/* View mode toggle */}
          <div className="flex bg-primary-theme-10 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-2 py-1 text-xs rounded-md ${
                viewMode === 'kanban' 
                  ? 'bg-secondary-theme text-black' 
                  : 'text-theme'
              }`}
            >
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-2 py-1 text-xs rounded-md ${
                viewMode === 'list' 
                  ? 'bg-secondary-theme text-black' 
                  : 'text-theme'
              }`}
            >
              Lijst
            </button>
          </div>
          {/* Add task button */}
          <Button
            size="sm"
            onClick={() => setShowAddForm(true)}
            className="h-7 text-xs bg-secondary-theme hover:bg-secondary-theme/80 text-black"
          >
            + Taak
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Task Form */}
        {showAddForm && renderAddTaskForm()}
        
        {/* Tasks in selected view mode */}
        {viewMode === 'kanban' ? renderKanbanView() : renderListView()}
        
        {/* Edit Modal */}
        {editingTask && renderEditTaskForm()}
      </CardContent>
    </Card>
  );
};

export default KanbanWidget; 